import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import IconButton from "@mui/material/IconButton";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import PersonIcon from "@mui/icons-material/Person";
import AddCommentIcon from "@mui/icons-material/AddComment";
import { useNotasCuentaCorriente } from "./useNotas";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");

export default function NotasCuentaCorrienteModal({
  open,
  onClose,
  nombreCliente,
  idCliente,
  envasado,
  refresh,
}) {
  const { notas, loading, crear, editar, eliminar } = useNotasCuentaCorriente(
    idCliente,
    envasado,
    open,
  );

  const [nuevaNota, setNuevaNota] = useState("");

  const [editando, setEditando] = useState(null);
  const [textoEditado, setTextoEditado] = useState("");

  const [notaAEliminar, setNotaAEliminar] = useState(null);

  const formatRelative = (fecha) => {
    return dayjs(fecha).fromNow();
  };

  const ultimaNota = notas[0];
  const totalNotas = notas.length;
  const username = localStorage.getItem("user");
  const handleGuardar = async () => {
    if (!nuevaNota.trim()) return;

    try {
      await crear({
        nota: nuevaNota,
        user: username,
      });
      setNuevaNota("");
      refresh();
    } catch (error) {
      console.error(error);
    }
  };

  const comenzarEdicion = (nota) => {
    setEditando(nota.id);
    setTextoEditado(nota.nota);
  };

  const cancelarEdicion = () => {
    setEditando(null);
    setTextoEditado("");
  };
  const handleClose = () => {
    onClose();
    refresh();
  };

  const guardarEdicion = async () => {
    const texto = textoEditado.trim();

    if (!texto) return;

    try {
      await editar(editando, texto);

      setEditando(null);
      setTextoEditado("");
      refresh();
    } catch (err) {
      console.error(err);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminar(notaAEliminar);
      setNotaAEliminar(null);
      refresh();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <StickyNote2Icon sx={{ color: "#FFD54F" }} />
            <Typography variant="h6">Notas de Cuenta Corriente</Typography>
          </Stack>

          <Chip label={`${totalNotas} notas`} color="warning" size="small" />
        </DialogTitle>

        <DialogContent sx={{ bgcolor: "#FAFAFA", pt: 3 }}>
          {/* ═══════════════════════════════════════
            RESUMEN "PREMIUM"
            ═══════════════════════════════════════ */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              m: 3,
              borderRadius: 2,
              bgcolor: "#E3F2FD",
              border: "1px solid #90CAF9",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <PersonIcon fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={700} color="primary.dark">
                Cliente: {nombreCliente}
              </Typography>
            </Stack>

            <Divider sx={{ borderColor: "#90CAF9", mb: 1 }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.5, sm: 2 }}
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary">
                Total de notas: <strong>{totalNotas}</strong>
              </Typography>

              {ultimaNota && (
                <Typography variant="caption" color="text.secondary">
                  Última actualización{" "}
                  <strong>
                    {formatRelative(ultimaNota.updated_at)} por{" "}
                    {ultimaNota.user}
                  </strong>
                </Typography>
              )}
            </Stack>
          </Paper>

          {/* ═══════════════════════════════════════
            LISTA DE NOTAS
            ═══════════════════════════════════════ */}
          <Box sx={{ maxHeight: 320, overflow: "auto", pr: 0.5 }}>
            {notas.map((nota) => (
              <Paper
                key={nota.id}
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  mb: 2,
                  transition: ".2s",
                  "&:hover": { boxShadow: 5 },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar
                    sx={{
                      bgcolor: nota.color,
                      width: 36,
                      height: 36,
                      fontSize: 14,
                    }}
                  >
                    {nota.user[0]}
                  </Avatar>

                  <Box flex={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography fontWeight={700} variant="body2">
                        {nota.user}
                      </Typography>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip
                          title={dayjs(nota.created_at).format(
                            "DD/MM/YYYY HH:mm",
                          )}
                          arrow
                        >
                          <Typography variant="caption" color="text.secondary">
                            {nota.editado
                              ? `${formatRelative(nota.updated_at)} · Editado`
                              : formatRelative(nota.created_at)}
                          </Typography>
                        </Tooltip>

                        {nota.user === username && (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => comenzarEdicion(nota)}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setNotaAEliminar(nota.id)}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Stack>

                    {editando === nota.id ? (
                      <>
                        <TextField
                          multiline
                          fullWidth
                          minRows={3}
                          value={textoEditado}
                          onChange={(e) => setTextoEditado(e.target.value)}
                        />

                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          sx={{ mt: 2 }}
                        >
                          <Button
                            startIcon={<CloseRoundedIcon />}
                            onClick={cancelarEdicion}
                          >
                            Cancelar
                          </Button>

                          <Button
                            variant="contained"
                            startIcon={<CheckRoundedIcon />}
                            onClick={guardarEdicion}
                            disabled={!textoEditado.trim()}
                          >
                            Guardar
                          </Button>
                        </Stack>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          mt: 1,
                          whiteSpace: "pre-wrap",
                          fontSize: "0.9rem",
                        }}
                      >
                        {nota.nota}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>

          {/* ═══════════════════════════════════════
            NUEVA NOTA
            ═══════════════════════════════════════ */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 2,
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor: "#90CAF9",
              bgcolor: "#FAFCFF",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <AddCommentIcon color="primary" />
              <Typography fontWeight={700}>Nueva nota</Typography>
            </Stack>

            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="Escriba aquí una observación sobre el cliente..."
              value={nuevaNota}
              onChange={(e) => setNuevaNota(e.target.value)}
              sx={{ bgcolor: "white", borderRadius: 1 }}
            />
          </Paper>
        </DialogContent>

        {/* ═══════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════ */}
        <DialogActions
          sx={{
            bgcolor: "#F8F9FA",
            px: 3,
            py: 2,
          }}
        >
          <Button onClick={handleClose}>Cancelar</Button>

          <Button
            variant="contained"
            startIcon={<StickyNote2Icon />}
            color="warning"
            onClick={handleGuardar}
            disabled={!nuevaNota.trim()}
          >
            Guardar nota
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(notaAEliminar)}
        onClose={() => setNotaAEliminar(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "error.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 2,
          }}
        >
          <DeleteForeverIcon sx={{ fontSize: 30 }} />

          <Box>
            <Typography variant="h6" fontWeight={700}>
              Eliminar nota
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,.85)",
              }}
            >
              Esta acción requiere confirmación
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            py: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" fontWeight={600} gutterBottom>
              ¿Está seguro que desea eliminar esta nota?
            </Typography>

            <Typography variant="body2" color="text.secondary">
              La nota dejará de visualizarse para todos los usuarios de Cuenta
              Corriente.
            </Typography>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: "#F8F9FA",
            px: 3,
            py: 2,
          }}
        >
          <Button variant="outlined" onClick={() => setNotaAEliminar(null)}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={confirmarEliminar}
          >
            Eliminar nota
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
